import { STREAM_CATEGORIES, type PublicStream, type StreamCategory } from '@zip2live/shared';
import { EyeIcon, SparkleIcon } from './Icons.tsx';

const LABELS = new Map<string, string>(STREAM_CATEGORIES.map((c) => [c.id, c.label]));

export function categoryLabel(id: StreamCategory | string | null | undefined): string | null {
  return id ? (LABELS.get(id) ?? null) : null;
}

const formatter = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
/** 980, 12K, 1.2M. */
export const count = (n: number) => (n < 10_000 ? n.toLocaleString() : formatter.format(n));

const isLive = (status: string) => status === 'LIVE' || status === 'RECONNECTING';

/** For a stream with no preview image yet. */
export const CATEGORY_EMOJI: Record<string, string> = {
  gaming: '🎮', music: '🎵', talk: '💬', sports: '🏀', fitness: '💪', food: '🍳',
  outdoors: '🌆', creative: '🎨', learning: '📚', tabletop: '♟️', animals: '🐾', other: '✨',
};

/** "1 h 12 min", "42 min", "under a minute". */
export function duration(startedAt: number | null, endedAt: number | null): string | null {
  if (!startedAt || !endedAt || endedAt <= startedAt) return null;
  const minutes = Math.round((endedAt - startedAt) / 60_000);
  if (minutes < 1) return 'under a minute';
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ''}`;
}

/** Eye and how many are watching now, for over the video next to the LIVE badge. */
export function WatchingPill({ viewers, className = '' }: { viewers: number; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg bg-black/50 px-2.5 py-1 text-sm font-bold text-white tabular-nums ${className}`} aria-label={`${viewers} watching now`}>
      <EyeIcon />
      {count(viewers)}
    </span>
  );
}

/** What the AI scan says the stream is about. */
export function CategoryTag({ category, tone = 'dark' }: { category: StreamCategory | string | null | undefined; tone?: 'dark' | 'light' }) {
  const label = categoryLabel(category);
  if (!label) return null;
  const colors = tone === 'dark' ? 'bg-white/10 text-white' : 'bg-mist text-navy';
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-extrabold ${colors}`} title="Category tagged by AI from the stream">
      <SparkleIcon />
      {label}
    </span>
  );
}

/**
 * "12 watching · Peak 25 · 340 views" and the AI category. Watching only while live; nothing at
 * all for a stream nobody has opened yet. Once it has ended, the after-stream stats: how long it
 * ran, likes (and dislikes, for its creator) and reactions.
 */
export function StreamStats({ stream, tone = 'dark', className = '' }: {
  stream: Pick<PublicStream, 'status' | 'viewers' | 'peakViewers' | 'views' | 'category' | 'startedAt' | 'endedAt' | 'likes' | 'dislikes' | 'reactions'>;
  tone?: 'dark' | 'light';
  className?: string;
}) {
  const live = isLive(stream.status);
  const ended = stream.status === 'ENDED';
  const plural = (n: number, one: string, many: string) => `${count(n)} ${n === 1 ? one : many}`;
  const parts = [
    live ? `${count(stream.viewers)} watching` : null,
    stream.peakViewers > 0 ? `Peak ${count(stream.peakViewers)}` : null,
    stream.views > 0 || live ? plural(stream.views, 'view', 'views') : null,
    ended ? duration(stream.startedAt, stream.endedAt) : null,
    ended && stream.likes > 0 ? plural(stream.likes, 'like', 'likes') : null,
    ended && (stream.dislikes ?? 0) > 0 ? plural(stream.dislikes!, 'dislike', 'dislikes') : null,
    ended && stream.reactions > 0 ? plural(stream.reactions, 'reaction', 'reactions') : null,
  ].filter(Boolean);
  if (!parts.length && !stream.category) return null;
  return (
    <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-sm ${tone === 'dark' ? 'text-muted-dark' : 'text-muted'} ${className}`}>
      {parts.length > 0 && <span className="tabular-nums">{parts.join(' · ')}</span>}
      <CategoryTag category={stream.category} tone={tone} />
    </div>
  );
}
